import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Link } from "react-router-dom";
import PageContainer from "../../../components/PageContainer";
import DataTable from "../../../components/DataTable";
import EmptyState from "../../../components/EmptyState";
import EmptyValue from "../../../components/EmptyValue";
import TableSkeleton from "../../../components/TableSkeleton";
import ChipSingleSelect from "../../../components/filters/ChipSingleSelect";
import SearchInput from "../../../components/filters/SearchInput";
import Pagination from "../../../components/Pagination";
import useProcurementTable from "./useProcurementTable";
import ReceiveModal from "./ReceiveModal";
import BatchStockModal from "./BatchStockModal";
import ProcurementCart from "./cart/ProcurementCart";
import {
    ProcurementTabs,
    FILTER_CHIPS,
    PARAM_FILTER,
    PARAM_PAGE,
    ShortagesOnlyToggle,
    LookaheadSelect,
    PageSizeSelect,
    getViewDemand,
    getShortfall,
    getSource,
    StatusBadge,
    RowAction,
    ViewBatchesButton,
    LedgerLink,
    OrderIdPills,
    PartialViewNotice,
    SuggestedQuantity,
    SplitVendorNotice,
    StripSizeInput,
    UnavailableBadge,
    StockHealthNotice,
    StockTotalNotice,
} from "./shared";

// Procurement — the shortage screen (GET /api/inventory/shortages). Overall and New
// share this ONE table shape (Reorder is a structurally different, prototype-matched
// table — see ReorderProcurementView.jsx) and are BOTH fully actionable: checkbox,
// vendor picker, status badge, per-row "Mark Requested", and the bulk select flow.
// New's column set is Overall's minus the Source column (New has no A/B split to
// attribute a source to — Source only makes sense once New and Reorder are combined).
//
//   New      — committed demand (N) only, buffer included: max(N + S - stock, 0).
//              Its own purchase figure is UNROUNDED (a raw tablet gap, not a strip
//              multiple) — see shared.js's getViewPurchase.
//   Overall  — everything: strips = ceil(max(N + A + v*B + S - stock, 0) / S). The +S
//              buffer is UNCONDITIONAL (2026-09-04 product-confirmed correction — see
//              IMPLEMENTATION.md §2): "after covering ALL demand, at least one full
//              strip must remain in hand" is a statement about the residual, not about
//              which bucket the demand came from. New ALSO always carries +S (its own
//              formula includes it) — only Reorder (A + v*B, no committed demand of its
//              own to hold a reorder point against) has no buffer at all.
//
// New and Reorder each net the FULL stock independently and deliberately do NOT sum to
// Overall (both count the same stock once each; Overall counts it exactly once) — see
// shared.js's PARTIAL_VIEW_COPY, shown under the heading on both partial views.
//
// A medicine appears on a view whenever it has demand in that slice, even when the
// resulting purchase figure is 0 — see shared.js's isRowInView.
function ProcurementView({ view }) {
    const isOverall = view === "overall";
    const {
        isLoading,
        isFetching,
        isError,
        refetch,
        readAt,
        lapsingEnabled,
        isRowUnavailable,
        getSuggestedQuantity,
        viewRows,
        visibleRows,
        chipCounts,
        filterValue,
        searchParams,
        setSearchParams,
        batchesRow,
        setBatchesRow,
        receivingRow,
        setReceivingRow,
        settingStripSizeItemId,
        handleSetStripSize,
        page,
        pageSize,
        total,
        setPage,
        shortOnly,
        setShortOnly,
        lookaheadDays,
        setLookaheadDays,
        countsAreForPage,
        setPageSize,
        searchValue,
        setSearch,
    } = useProcurementTable(view);

    const columns = [
        { key: "medicineName", label: "Medicine", render: (row) => <span className="font-semibold">{row.medicineName || <EmptyValue />}</span> },
        {
            key: "stripSize",
            label: "Strip size",
            render: (row) => (row.stripSize != null ? `${row.stripSize} / strip` : <EmptyValue />),
        },
        {
            key: "demand",
            label: "Cumulative qty needed",
            render: (row) => (row.demandUnknown ? <EmptyValue /> : getViewDemand(row, view, lapsingEnabled)),
        },
        {
            key: "onHand",
            label: "Qty present",
            render: (row) => {
                if (isRowUnavailable(row)) {
                    return (
                        <>
                            <EmptyValue />
                            <ViewBatchesButton row={row} onClick={setBatchesRow} />
                        </>
                    );
                }
                const shortfall = getShortfall(row, view, lapsingEnabled);
                return (
                    <>
                        {row.onHand}
                        {shortfall > 0 && <div className="text-[10px] text-[var(--ops-carbon-500)]">short by {shortfall}</div>}
                        <ViewBatchesButton row={row} onClick={setBatchesRow} />
                    </>
                );
            },
        },
        { key: "inTransit", label: "In transit", render: (row) => (isRowUnavailable(row) ? <EmptyValue /> : row.inTransit) },
        // Fix (2026-09-05): "Orders linked" was gated behind Overall only — it belongs on
        // every view, matching the prototype's New list (PE-1005/PE-1001 pills).
        { key: "orderIds", label: "Orders linked", render: (row) => <OrderIdPills orderIds={row.orderIds} /> },
        {
            key: "purchase",
            label: "Purchase qty (+1 strip)",
            render: (row) => {
                // §Phase-7-UI (2026-09-09) — a missing strip size is one of the two
                // reasons a row is stockUnknown (§3.2); it gets its OWN affordance
                // (StripSizeInput) rather than the generic "unknown" pill, since it's
                // the one unknown-data case a pharmacist can fix right here. Checked
                // BEFORE isRowUnavailable so it always wins even if onHand is ALSO
                // unknown for this row.
                if (row.stripSize == null) {
                    return (
                        <StripSizeInput
                            row={row}
                            onSet={handleSetStripSize}
                            isSaving={settingStripSizeItemId === row.itemId}
                        />
                    );
                }
                if (isRowUnavailable(row)) return <UnavailableBadge row={row} />;
                // Read-only suggestion (2026-10-27) — the quantity is chosen in the
                // cart, where each line can show the medicine's batch-wise stock. Only
                // Overall/New carry the "+1 strip buffer" (Reorder has no buffer at all).
                return <SuggestedQuantity row={row} suggested={getSuggestedQuantity(row)} showBufferNote />;
            },
        },
        { key: "ledgerUrl", label: "Ledger", render: (row) => <LedgerLink row={row} /> },
    ];

    if (isOverall && lapsingEnabled) {
        columns.push({
            key: "source",
            label: "Source",
            render: (row) => (row.demandUnknown ? <EmptyValue /> : getSource(row) || <EmptyValue />),
        });
    }

    columns.push(
        { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} /> },
        {
            key: "action",
            label: "Action",
            render: (row) => (
                <RowAction
                    row={row}
                    onReceive={setReceivingRow}
                    cartQuantity={isRowUnavailable(row) ? undefined : getSuggestedQuantity(row) ?? undefined}
                    view={view}
                    lapsingEnabled={lapsingEnabled}
                    lookaheadDays={view === "new" ? undefined : lookaheadDays}
                />
            ),
        }
    );

    return (
        <PageContainer>
            <header className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-[22px] font-bold text-[var(--ops-carbon-900)]">Procurement</h1>
                    {readAt && (
                        <p className="mt-1 text-xs text-[var(--ops-carbon-500)]">Stock read {moment(readAt).fromNow()}</p>
                    )}
                </div>
                <div className="flex items-center gap-4">
                    <Link
                        to="/v2/inventory/procurement/purchase-orders"
                        className="text-sm font-semibold text-[var(--ops-emerald-600)] hover:underline"
                    >
                        Purchase orders →
                    </Link>
                    <button
                        type="button"
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[10px] border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] text-[var(--ops-carbon-900)] font-semibold text-[13px] disabled:opacity-70 disabled:cursor-wait enabled:hover:bg-[var(--ops-emerald-50)] enabled:hover:border-[var(--ops-emerald-200)]"
                    >
                        {isFetching ? "Refreshing…" : "Refresh"}
                    </button>
                </div>
            </header>

            <ProcurementTabs />

            {!isOverall && <PartialViewNotice />}

            <StockHealthNotice rows={viewRows} />
            <StockTotalNotice rows={viewRows} />

            <SplitVendorNotice />

            <section aria-label="Filters" className="mt-4 rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] p-4">
                <ChipSingleSelect
                    label="Filter"
                    inline
                    options={FILTER_CHIPS.map((chip) => ({ id: chip.id, label: `${chip.label} (${chipCounts[chip.id] ?? 0})` }))}
                    value={filterValue}
                    onChange={(nextFilter) => {
                        const next = new URLSearchParams(searchParams);
                        if (nextFilter && nextFilter !== "all") next.set(PARAM_FILTER, nextFilter);
                        else next.delete(PARAM_FILTER);
                        next.delete(PARAM_PAGE);
                        setSearchParams(next, { replace: true });
                    }}
                />
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="flex-1 min-w-[220px]">
                        <SearchInput value={searchValue} onChange={setSearch} placeholder="Search a medicine by name" />
                    </div>
                    {view !== "new" && <LookaheadSelect value={lookaheadDays} onChange={setLookaheadDays} />}
                    <ShortagesOnlyToggle checked={shortOnly} onChange={setShortOnly} />
                    {countsAreForPage && (
                        <span className="text-[11px] text-[var(--ops-carbon-500)]">Counts show the shortages on this page only.</span>
                    )}
                </div>
            </section>

            {isLoading ? (
                <div className="mt-4 w-full overflow-x-auto rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]">
                    <table className="w-full min-w-full border-collapse text-sm">
                        <tbody>
                            <TableSkeleton rows={10} cols={columns.length} />
                        </tbody>
                    </table>
                </div>
            ) : isError ? (
                <div className="mt-4 rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]">
                    <EmptyState title="Couldn't load shortages" hint="Something went wrong reaching the service. Try again in a moment." />
                </div>
            ) : !isOverall && !lapsingEnabled ? (
                <div className="mt-4 rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]">
                    <EmptyState
                        title="This view needs the lapsing-kit prediction turned on"
                        hint="New and Reorder split committed demand from predicted demand — enable INVENTORY_PROCUREMENT_LAPSING to see them."
                    />
                </div>
            ) : visibleRows.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]">
                    <EmptyState title="No shortages" hint="Nothing needs ordering in this view right now." />
                </div>
            ) : (
                <DataTable columns={columns} rows={visibleRows} emptyCell={<EmptyValue />} />
            )}

            {!isLoading && !isError && total > 0 && (
                <div className="mt-4">
                    <div className="flex justify-end mb-2">
                        <PageSizeSelect value={pageSize} onChange={setPageSize} />
                    </div>
                    {total > pageSize && <Pagination page={page} size={pageSize} total={total} onChange={setPage} />}
                </div>
            )}

            <ProcurementCart />
            {batchesRow && <BatchStockModal row={batchesRow} view={view} lapsingEnabled={lapsingEnabled} lookaheadDays={view === "new" ? undefined : lookaheadDays} onClose={() => setBatchesRow(null)} />}
            {receivingRow && <ReceiveModal row={receivingRow} onClose={() => setReceivingRow(null)} />}
        </PageContainer>
    );
}

ProcurementView.propTypes = {
    view: PropTypes.oneOf(["new", "overall"]).isRequired,
};

export default ProcurementView;
